import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Star, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';

const LoungeReviewsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [lounge, setLounge] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLounge = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lounges/${id}`);
      setLounge(res.data);
    } catch (error) {
      toast.error('Failed to load lounge');
    }
  }, [id]);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lounges/${id}/reviews`);
      setReviews(res.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLounge();
    fetchReviews();
  }, [fetchLounge, fetchReviews]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={star <= Math.round(rating) 
              ? 'text-amber-500 fill-amber-500' 
              : 'text-white/20'}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0D0D0F] pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-[#0D0D0F]/95 backdrop-blur-sm border-b border-white/10 z-10">
        <div className="flex items-center gap-3 p-4">
          <button
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-white truncate">
              Reviews
            </h1>
            {lounge && (
              <p className="text-white/50 text-sm truncate">{lounge.name}</p>
            )}
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12">
            <Star className="mx-auto text-white/20 mb-3" size={48} />
            <p className="text-white/50">No reviews yet</p>
            <p className="text-white/30 text-sm mt-1">
              Be the first to rate this lounge!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="bg-white/5 border border-white/10 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  {/* User Avatar */}
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                    {review.user_profile_pic ? (
                      <img 
                        src={review.user_profile_pic} 
                        alt={review.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User size={20} className="text-white/40" />
                      </div>
                    )}
                  </div>
                  
                  {/* Review Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <button
                        onClick={() => navigate(`/user/${review.user_id}`)}
                        className="text-white font-medium text-sm hover:text-amber-500 transition-colors truncate"
                      >
                        {review.username}
                      </button>
                      <span className="text-white/30 text-xs flex-shrink-0">
                        {formatDate(review.created_at)}
                      </span>
                    </div>
                    
                    <div className="mb-2">
                      {renderStars(review.rating)}
                    </div>
                    
                    {review.comment && (
                      <p className="text-white/70 text-sm">{review.comment}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default LoungeReviewsPage;
